// src/components/Pages/MenuPage.js
import React, { useMemo } from 'react';
import {
  Container,
  Title,
  Text,
  SimpleGrid,
  Box,
  useMantineTheme,
  useMantineColorScheme
} from '@mantine/core';
import {
  IconBulb,
  IconExternalLink,
  IconCalendar,
  IconCreditCard,
  IconBook,
  IconChartLine,
  IconWand,
  IconClipboardText,
  IconUsersGroup, IconSend, IconRadar2, IconReportAnalytics,
  IconBan, IconPencilCheck, IconCalendarTime, IconPencilSearch, IconVideo, IconBinaryTree
} from '@tabler/icons-react';
import MenuCard from '../Menu/MenuCard';

import { useBackgroundImage } from '../../context/BackgroundImageContext';
import backgroundImageFile from '../../assets/images/brochure-background.jpg';
import { useAuth } from '../../context/AuthContext';

const baseMenuItems = [
  { id: 'guidelines', title: '01 | Review Guidelines', description: 'View style guides and check the publishing non-negotiables.', icon: IconBook, linkTo: '/guidelines', type: 'internal' },
  { id: 'discovery', title: '02 | Get Inspired', description: 'Generate a thematic set of ideas using Title Discovery.', icon: IconWand, linkTo: '/strategic-titles', type: 'internal' },
  { id: 'fics', title: '03 | Check Ideas', description: 'Validate your concept against the archive, drafts and competitors.', icon: IconBulb, linkTo: '/fics', type: 'internal' },
  { id: 'briefs', title: '04 | Build Briefs', description: 'Convert ideas into a structured gallery plan for assignment.', icon: IconClipboardText, linkTo: '/brief-builder', type: 'internal' },
  { id: 'talent', title: '05 | Manage Briefs', description: 'Review built briefs. Log commission recipients and notes.', icon: IconUsersGroup, linkTo: '/manage-briefs', type: 'internal' },
  { id: 'dispatched-dashboard', title: '06 | Dispatched Dash', description: "View all commissioned briefs currently dispatched to writers.", icon: IconSend, linkTo: '/dispatched-briefs', type: 'internal' },
  { id: 'drafts', title: '07 | Upload Draft', description: 'Use the Guideline Checker to assess draft content.', icon: IconPencilSearch, linkTo: '#', type: 'placeholder' },
  { id: 'publish', title: '08 | Publish Content', description: 'Enter your article and launch it in the Love Inc. CMS.', icon: IconExternalLink, linkTo: 'http://controlcentre.loveincorporated.com/', type: 'external' },
  { id: 'schedule', title: '09 | Review Content', description: 'Schedule and edit articles in the Love Inc. CMS.', icon: IconExternalLink, linkTo: 'http://controlcentre.loveincorporated.com/', type: 'external' },
  { id: 'calendar', title: '10 | Manage Calendar', description: 'View the editorial calendar, upcoming assignments and deadlines.', icon: IconCalendar, linkTo: '/manage-calendar', type: 'internal' },
  { id: 'refresh', title: '11 | Resell Radar', description: 'Run an assessment of ageing articles. Select timely resells and add them to the pipeline.', icon: IconRadar2, linkTo: '/resell-radar', type: 'internal' },
  { id: 'resell-preparation', title: '12 | Resell Preparation', description: 'Prepare resell candidates for republication. Includes title suggestions.', icon: IconPencilCheck, linkTo: '/resell-preparation', type: 'internal' },
  { id: 'resell-scheduling', title: '13 | Resell Scheduling', description: 'Set resell dates/times and push final candidates to the CMS.', icon: IconCalendarTime, linkTo: '/resell-scheduling', type: 'internal' },
  { id: 'resell-blacklist', title: '14 | Resell Blacklist', description: 'Review blocked or set-for-update items that have been removed from the pipeline process.', icon: IconBan, linkTo: '/resell-blacklist', type: 'internal' },
  { id: 'video-engine', title: '15 | Craft Video', description: 'Generate a video preview based upon any existing gallery. Commission your MP4.', icon: IconVideo, linkTo: '/lab/video-producer', type: 'internal' },
  { id: 'video-library', title: '16 | Video Library', description: 'Real-time render updates. Showcases MP4s generated from gallery assets.', icon: IconVideo, linkTo: '/lab/video-library', type: 'internal' },
  { id: 'intelligence', title: '17 | Access Intel', description: 'Content performance, syndication insight, and much more.', icon: IconChartLine, linkTo: 'https://app.powerbi.com/groups/me/reports/a989e8ca-a356-4201-84d2-face8055a34d/ReportSection9ccd1f01359e6bd5beee?ctid=dda7cbe6-3af9-43d0-8a9e-128baf611c43&experience=power-bi', type: 'external' },
  // { id: 'paid', title: '18 | Get Paid', description: 'Submit invoices and track your payment status.', icon: IconCreditCard, linkTo: '', type: 'placeholder' },
  { id: 'paid', title: '18 | Epistemic Graph', description: 'Visualise entities and facts extracted from the CMS archive in 3D.', icon: IconBinaryTree, linkTo: '/knowledge-graph', type: 'internal' },
];

function MenuPage() {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  // const isDark = colorScheme === 'dark'; // Not used in this snippet but kept for consistency
  const { isBackgroundImageVisible } = useBackgroundImage();
  const { userProfile } = useAuth();
  const headerTextColor = isBackgroundImageVisible ? theme.white : undefined;

  const menuItems = useMemo(() => {
    // 1. Identify Roles
    const hasPrivilegedRole = userProfile && ['admin', 'management'].includes(userProfile.role);

    // 2. RESTRICTED VIEW (Editors/Writers)
    if (!hasPrivilegedRole) {
      // Define the IDs allowed for Editors
      const allowedIds = ['refresh', 'resell-preparation', 'resell-scheduling', 'resell-blacklist', 'video-engine', 'video-library'];

      // Filter and clean up titles
      return baseMenuItems
        .filter(item => allowedIds.includes(item.id))
        .map(item => {
          // Manual title cleaning to remove numbers (e.g. "12 | ")
          let cleanTitle = item.title;
          if (item.id === 'refresh') cleanTitle = 'Resell Radar';
          if (item.id === 'resell-preparation') cleanTitle = 'Resell Preparation';
          if (item.id === 'resell-scheduling') cleanTitle = 'Resell Scheduling';
          if (item.id === 'resell-blacklist') cleanTitle = 'Resell Blacklist';
          if (item.id === 'video-engine') cleanTitle = 'Craft Video';
          if (item.id === 'video-library') cleanTitle = 'Video Library';

          return { ...item, title: cleanTitle };
        });
    }

    // 3. FULL VIEW (Admin/Management)
    return baseMenuItems.map(item => {
      // Disable Dispatched Dashboard for non-admins (if that logic still holds for management)
      if (item.id === 'dispatched-dashboard' && !hasPrivilegedRole) {
        return { ...item, type: 'disabled' };
      }
      return item;
    });
  }, [userProfile]);

  return (
    <Box
      style={{
        width: '100%',
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        backgroundImage: isBackgroundImageVisible ? `url(${backgroundImageFile})` : 'none',
        backgroundSize: isBackgroundImageVisible ? 'cover' : undefined,
        backgroundPosition: isBackgroundImageVisible ? 'center' : undefined,
        backgroundRepeat: isBackgroundImageVisible ? 'no-repeat' : undefined,
        backgroundAttachment: isBackgroundImageVisible ? 'fixed' : undefined,
        overflow: 'auto',
      }}
    >
      <Container
        py="xl"
        size="lg"
        style={{
          width: '100%',
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Title order={1} ta="center" mb="xs" style={{ color: headerTextColor }}>
          Writer Hub Menu
        </Title>
        <Text ta="center" mb="xl" style={{ color: headerTextColor }}>
          Select an option below to proceed...
        </Text>

        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="xl" verticalSpacing="xl">
          {menuItems.map((item) => (<MenuCard key={item.id} item={item} />))}
        </SimpleGrid>
      </Container>
    </Box>
  );
}

export default MenuPage;